import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BoardEntry, Side } from '../lib/match-snapshot';
import { BoardSideLabel, boardSideLabel } from './BoardSideLabel';

export type SceneRect = { x: number; y: number; width: number; height: number };
export type ToolPlacement = SceneRect & { dock: string };
export function overlaps(a: SceneRect, b: SceneRect, clearance = 0): boolean {
  return a.x < b.x + b.width + clearance && a.x + a.width + clearance > b.x && a.y < b.y + b.height + clearance && a.y + a.height + clearance > b.y;
}

/** Keep desktop bowls beside the board; small screens retain the shallow rim overlap. */
export function placeTools(width: number, height: number, board: SceneRect, protectedRects: SceneRect[], tray = false): ToolPlacement | null {
  const desktop=width>=760;
  const ratio=tray ? .66 : 11/12;
  const sizingRatio=tray ? .66 : 5/6; // Keep the approved bowl width after expanding the asset canvas.
  const besideBoard = desktop && !tray;
  const w = besideBoard
    ? Math.min(260 * .92, Math.max(128, board.width * .5), width - board.x - board.width - 20)
    : (desktop?260:Math.min(180,width*.47,(height-board.y-board.height-8)/(sizingRatio*.88)))*(tray?1:.92);
  if(w<100)return null;
  const h=w*ratio;
  const rimInset=tray?0:w*(18/720); // Baked top margin; position the actual rim, not the image box.
  const candidate = besideBoard
    ? {x:board.x+board.width+12,y:Math.min(height-h-8,board.y+board.height-h*.8),width:w,height:h,dock:'board-edge'}
    : {x:width-w*.62,y:desktop?Math.min(height-h-8,board.y+board.height*.55)-(tray?0:14):board.y+board.height-(tray?-8:6),width:w,height:h,dock:'screen-edge'};
  candidate.y-=rimInset;
  // Allow only the bowl rim over the lower wooden edge, keeping grid/labels clear.
  const visible={...candidate,width:Math.min(candidate.width,width-candidate.x)};
  const protectedBoard=tray?board:{...board,height:Math.max(0,board.height-12)};
  if(candidate.y<0||overlaps(visible,protectedBoard,tray?8:0)||protectedRects.some(rect=>overlaps(visible,rect,8)))return null;
  return candidate;
}

export function BoardToolsScene({ entry, yourSide }: { entry: BoardEntry; yourSide: Side | null }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [clip, setClip] = useState({left:0,top:0,width:0,height:0});
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<ToolPlacement | null>(null);
  const sample = useMemo(() => boardSideLabel(entry, yourSide), [entry, yourSide]);
  useLayoutEffect(() => {
    const slot = anchor.current?.parentElement;
    const stage = slot?.querySelector<HTMLElement>('.play-active-stage');
    if (!slot || !stage || !sample) { setPlacement(null); return; }
    const shell=slot.closest<HTMLElement>('.play-shell');
    if(!shell)return;
    setHost(shell);
    let frame = 0;
    const measure = () => {
      if (stage.getAnimations().some(animation => animation.playState === 'running')) { setPlacement(null); return; }
      const bounds = slot.getBoundingClientRect();
      const localRect = (el: Element): SceneRect => {
        const rect = el.getBoundingClientRect();
        return { x: rect.left, y: rect.top - bounds.top, width: rect.width, height: rect.height };
      };
      const board = stage.querySelector('.jungo-board,.renju-board,.connect6-board,.gomoku-board,.reversi-board,.chess-board,.amazons-grid,.abalone-board,.twixt-board,.draughts-board,.trax-board');
      if (!board || bounds.width < 1) { setPlacement(null); return; }
      // Intersections and non-board controls stay clear; the lower wood edge may overlap.
      const obstacles = [...stage.querySelectorAll('button:not(.jungo-intersection), .jungo-scorebar, .board-actions-bar, .amazons-remaining, .abalone-scores')]
        .map(localRect).filter(rect => rect.width > 0 && rect.height > 0);
      const clipHeight=Math.max(bounds.height,window.innerHeight-bounds.top-68);
      setClip({left:0,top:bounds.top,width:window.innerWidth,height:clipHeight});
      const next = placeTools(window.innerWidth, clipHeight, localRect(board), obstacles, sample.vessel !== 'bowl');
      setPlacement(old => JSON.stringify(old) === JSON.stringify(next) ? old : next);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    const resize = new ResizeObserver(schedule);
    resize.observe(slot); resize.observe(stage); if (stage.firstElementChild) resize.observe(stage.firstElementChild);
    const mutations = new MutationObserver(schedule);
    mutations.observe(stage, { childList: true, subtree: true, attributes: true });
    slot.addEventListener('animationend', schedule, true);
    window.addEventListener('resize', schedule);
    measure();
    return () => { cancelAnimationFrame(frame); resize.disconnect(); mutations.disconnect(); slot.removeEventListener('animationend', schedule, true); window.removeEventListener('resize', schedule); };
  }, [sample]);
  return <><span ref={anchor} hidden />{host && createPortal(<div className="board-tools-clip" style={{left:clip.left,top:clip.top,width:clip.width,height:clip.height}}>
    <div className="board-tools-scene" data-testid="board-tools-scene" data-dock={placement?.dock ?? 'hidden'}
      style={placement ? {left:placement.x,top:placement.y,width:placement.width,height:placement.height} : {visibility:'hidden'}}>
      {sample && <BoardSideLabel entry={entry} yourSide={yourSide} />}
    </div>
  </div>,host)}</>;
}
